App.namespace('messenger.UploadManager', () => {
  /**
     * Count progress.
     *
     * @param {Number} current
     * @param {Number} total
     * @returns {Number}
     */
  const countProgress = function (current, total) {
    return parseInt((current * 100) / total, 10);
  };

  /**
     * Unit of given time should be "seconds".
     *
     * @param {Number} current
     * @param {Number} next
     * @param {Number} time [time=20]
     * @returns {Number}
     */
  const countTimeForIncreaseByOnePoint = function (current, next, time) {
    time = time || 20;

    current = next - current;
    time = current / time;

    return Math.abs(
      parseFloat(time.toFixed(3)),
    );
  };

  /**
     * "this" should be instance of Manager.
     * Method updates progressbar.
     *
     * @param {number} progress
     * @returns {void}
     */
  const updateProgressbar = function (progress) {
    this.$queue.find('.value').text(progress);
    this.$queue.find('.bar').css('width', `${progress}%`);
  };

  const getAcceptedExtensions = function () {
    if (window.__TGV_MESSENGER_ACCEPTED_FILES) {
      return window.__TGV_MESSENGER_ACCEPTED_FILES.map((extension) => {
        return `.${extension}`;
      }).join(',');
    }
    return '';
  };

  const getAcceptedExtensionsPretty = function () {
    if (window.__TGV_MESSENGER_ACCEPTED_FILES) {
      return window.__TGV_MESSENGER_ACCEPTED_FILES.map((extension) => {
        return extension;
      })
        .join(', ');
    }

    return '';
  };

  const Manager = function ($container, $dropzoneContainer, $template, $queue, $itemsListContainer, options) {
    const self = this;

    this.$container = $container;
    this.$dropzoneContainer = $dropzoneContainer;
    this.$attachButton = $container.find('.js-btn-attach');
    this.$template = $template;
    this.$itemsListContainer = $itemsListContainer;
    this.$queue = $queue;
    this.$error = $container.find('.js-upload-error');
    this.options = options;
    this.elements = {};
    this.errors = [];
    this.acceptedFilesQueue = [];
    this.reloadOnComplete = false;
    this.totalFilesInQueue = 0;
    this.numberOfUploadedFiles = 0;
    this.cachedNumberOfUploadFile = 0;
    this.cachedNumberOfTotalFiles = 0;
    this.beginProgressValue = 0;
    this.timerId = null;
    this.beginTimerId = null;

    /**
         * @type {Dropzone}
         */
    this.uploader = new Dropzone(this.$dropzoneContainer[0], {
      url: options.upload_url,
      maxFilesize: 50,
      uploadMultiple: true,
      createImageThumbnails: false,
      previewTemplate: '<div style="display:none"></div>',
      autoProcessQueue: true,
      clickable: this.$attachButton[0],
      acceptedFiles: getAcceptedExtensions(),
      dictInvalidFileType: '{{filename}} has an unsupported file type.'
        + `\n Please use one of the following supported file types: ${getAcceptedExtensionsPretty()}`,
      dictFileTooBig: '{{filename}} is too large ({{filesize}} MB). The maximum file size is {{maxFilesize}} MB.',
      dragenter: (e) => self.onDrag(e),
      dragover: (e) => self.onDrag(e),
    });

    this.attachEvents();
  };

  /**
     * @returns {Manager}
     */
  Manager.prototype.acceptFilesQueue = function () {
    if (this.acceptedFilesQueue.length === 0) {
      return this;
    }

    this.acceptedFilesQueue.forEach((item) => {
      item.callback();
    });

    return this.clearAcceptedFilesQueue();
  };

  /**
     * Clear queue with files to upload.
     *
     * @returns {Manager}
     */
  Manager.prototype.clearAcceptedFilesQueue = function () {
    this.acceptedFilesQueue = [];
    return this;
  };

  Manager.prototype.onDrag = function (event) {
    if (event.dataTransfer.types.includes('Files')) {
      // https://github.com/dropzone/dropzone/blob/main/src/options.js#L587
      this.$dropzoneContainer[0].classList.add('dz-drag-hover');
    }
  };

  /**
     * Attach events for uploader.
     *
     * @returns {void}
     */
  Manager.prototype.attachEvents = function () {
    const self = this;
    this.uploader.on('sendingmultiple', () => {
      self.$attachButton.attr('disabled', 'disabled');
      self.$attachButton.hasLoadingState(true);
    });
    // When part of files has been uploaded
    this.uploader.on('successmultiple', (file, response) => {
      self.$attachButton.removeAttr('disabled');
      self.$attachButton.hasLoadingState(false);
      const files = response.data.success;
      const errors = response.data.error;

      self.$container.trigger('uploadedPhoto', response.data.meta);

      files.forEach(fileItem => {
        self.numberOfUploadedFiles += 1;
        self.addItem(fileItem);
        self.updateProcessing();
      });

      if (errors.length > 0) {
        self.addErrors(errors);
      }
    });

    // When something went wrong
    this.uploader.on('error', (file, response) => {
      self.totalFilesInQueue -= 1;

      if (self.totalFilesInQueue === 0) {
        self.$attachButton.removeAttr('disabled');
        self.$attachButton.hasLoadingState(false);
      }

      if (typeof response === 'string') {
        self.addErrors(response.replace('{{filename}}', `<strong>${file.name}</strong>`));
      } else if (response.error) {
        self.addErrors(`<strong>${file.name}</strong> - ${response.error}`);
      } else {
        self.addErrors(`<strong>${file.name}</strong> - An error occurred while uploading file. Please reload the page and try again.`);
      }

      Rollbar.warning('Messenger UploadManager error.', response);
    });

    // When each photos has been uploaded
    this.uploader.on('completemultiple', function (file) {
      this.removeFile(file);
    });

    // Before sending photos attach CSRF token into request
    this.uploader.on('sending', (file, xhr, formData) => {
      formData.append('_token', self.options.token);
    });

    // After chose images, when progress = 0 we have to make fake counter...
    this.uploader.on('sendingmultiple', () => {
      const clearBeginProgress = function () {
        if (this.beginTimerId === null) {
          return;
        }

        clearInterval(this.beginTimerId);
        this.beginTimerId = null;
      }.bind(this);

      if (this.cachedNumberOfUploadFile !== 0) {
        clearBeginProgress();
        return;
      }

      this.beginTimerId = setInterval(() => {
        this.beginProgressValue += 1;
        updateProgressbar.call(this, this.beginProgressValue);

        if (this.beginProgressValue > 10) {
          clearBeginProgress();
        }
      }, 500);
    });

    // When proccess of upload has been finished - show errors if exist and save new order of photos
    this.uploader.on('queuecomplete', () => {
      if (self.errors.length > 0) {
        self.showError();
      }

      if (self.options.onFinished) {
        self.options.onFinished();
      }

      setTimeout(() => {
        self.finishProcessing();
      }, 500);
    });

    // Increase counter after added new file
    this.uploader.on('addedfile', (file) => {
      if (self.options.onProcessing) {
        self.options.onProcessing(file);
      }
      self.totalFilesInQueue += 1;

      self.startProcessing();
    });

    // Increase counter after added new file
    this.uploader.on('removedfile', () => {
      if (self.options.onUploaded) {
        self.options.onUploaded();
      }
    });
  };

  /**
     * Add new error(s) to array.
     *
     * @param {string|string[]} errors
     * @returns {Manager}
     */
  Manager.prototype.addErrors = function (errors) {
    if (typeof errors === 'string') {
      errors = [errors];
    }

    $.merge(this.errors, errors);
    return this;
  };

  /**
     * Show progress bar. If progress bar is already visible, will update value of them.
     *
     * @returns {Manager}
     */
  Manager.prototype.startProcessing = function () {
    App.components.upload_manager_unload_prevention.add();
    if (!this.$queue.hasClass('processing')) {
      this.$queue.addClass('processing');
      $('#send').addClass('disabled');
      if ($('#preview_quote')) {
        $('#preview_quote').addClass('disabled');
      }
    } else {
      this.updateProcessing();
    }

    return this;
  };

  /**
     * Update progress bar.
     *
     * @returns {Manager}
     */
  Manager.prototype.updateProcessing = function () {
    // Sometimes this method is calling when `totalFilesInQueue` doesn't contain real number of uploaded files
    // This if "waiting" for all files before update progress bar
    if (this.cachedNumberOfTotalFiles !== this.totalFilesInQueue) {
      this.cachedNumberOfTotalFiles = this.totalFilesInQueue;
      return this;
    }

    // If file has been uploaded - "faker" counter should be reset
    if (this.cachedNumberOfUploadFile !== this.numberOfUploadedFiles) {
      this.cachedNumberOfUploadFile = this.numberOfUploadedFiles;
      this.clearInterval();
    }

    let loadedPercentage = countProgress(this.numberOfUploadedFiles, this.cachedNumberOfTotalFiles);
    let nextNumberOfUploadedFile = this.numberOfUploadedFiles + 2;

    // If real total number of files is lower than probably next number of uploaded file
    if (nextNumberOfUploadedFile > this.cachedNumberOfTotalFiles) {
      nextNumberOfUploadedFile = this.cachedNumberOfTotalFiles;
    }

    // Count next "breakpoint" for
    const maxUploadedPercentage = countProgress(nextNumberOfUploadedFile, this.cachedNumberOfTotalFiles);
    const timeInterval = countTimeForIncreaseByOnePoint(loadedPercentage, maxUploadedPercentage);

    updateProgressbar.call(this, loadedPercentage);

    this.timerId = setInterval(() => {
      if ((loadedPercentage + 1) > maxUploadedPercentage) {
        this.clearInterval();
      } else {
        loadedPercentage += 1;
      }

      if (loadedPercentage > 100) {
        loadedPercentage = 100;
        this.clearInterval();
      }

      updateProgressbar.call(this, loadedPercentage);
    }, timeInterval * 1000);

    return this;
  };

  /**
     * Reset and hide progress bar.
     *
     * @returns {Manager}
     */
  Manager.prototype.finishProcessing = function () {
    App.components.upload_manager_unload_prevention.remove();
    this.totalFilesInQueue = 0;
    this.numberOfUploadedFiles = 0;
    this.cachedNumberOfTotalFiles = 0;
    this.cachedNumberOfUploadFile = 0;
    this.clearInterval();

    this.$queue.removeClass('processing');
    $('#send').removeClass('disabled');
    if ($('#preview_quote')) {
      $('#preview_quote').removeClass('disabled');
    }

    setTimeout(() => {
      this.$queue.find('.value').text('0');
      this.$queue.find('.bar').css('width', 0);
    }, 50);

    return this;
  };

  /**
     * Clear cached timer.
     *
     * @returns {Manager}
     */
  Manager.prototype.clearInterval = function () {
    if (this.timerId === null) {
      return this;
    }

    clearInterval(this.timerId);
    this.timerId = null;

    return this;
  };

  /**
     * Add new item.
     *
     * @param {Object} fileDetails
     */
  Manager.prototype.addItem = function (fileDetails) {
    const id = fileDetails.id;
    if (id && this.elements[id]) {
      return;
    }

    const item = new App.messenger.ListItem(fileDetails, this.$itemsListContainer, this.options.entity);
    item.setManager(this);
    item.attachEvents();

    if (this.elements[item.getId()]) {
      return;
    }
    this.elements[item.getId()] = item;
    this.$container.append(item.getNode());
  };

  /**
     * Delete photo. If deleted photo was default - take next photo and set as default.
     *
     * @param {App.cabinet.rooms.PhotoItem} element
     */
  Manager.prototype.deleteItem = function (element) {
    const self = this;

    if (element.file_details && element.file_details.isTemplateFile) {
      element.getNode().hide('fast', function () {
        $(this).remove();
      });

      delete self.elements[element.getId()];
      if (self.options.onDeleted) {
        self.options.onDeleted();
      }
      return;
    }
    $.ajax({
      url: self.options.delete_url,
      method: 'POST',
      data: {
        _token: self.options.token,
        photoId: element.getId(),
      },
      success(response) {
        element.getNode().hide('fast', function () {
          $(this).remove();
        });

        delete self.elements[element.getId()];

        if (self.options.onAfterDeletion) {
          self.options.onAfterDeletion();
        }

        const data = {
          progress: response.data.progress,
          photos: response.data.photos,
        };

        self.$container.trigger('removedPhoto', data);

        if (undefined === response.data.default_photo_id) {
          return;
        }

        element = self.elements[response.data.default_photo_id];

        element.markAsDefault();
        element.getNode().prependTo(self.$itemsListContainer);
      },
    });

    if (self.options.onDeleted) {
      self.options.onDeleted();
    }
  };

  /**
     * @returns {Object<Number, App.cabinet.rooms.PhotoItem>}
     */
  Manager.prototype.getElements = function () {
    return this.elements;
  };

  Manager.prototype.getFilesArray = function () {
    const elements = this.getElements();
    const ids = this.getElementIds();

    return ids.map((id) => {
      return elements[id].file_details;
    });
  };

  /**
     * @returns {Object<Number, App.cabinet.rooms.PhotoItem>}
     */
  Manager.prototype.getElementIds = function () {
    return Object.keys(this.elements).map((key) => {
      return +key;
    });
  };

  /**
     * Show errors in container and clear errors list after display.
     *
     * @returns {Manager}
     */
  Manager.prototype.showError = function () {
    const $message = $('<p>').html(this.errors.join('<br/>'));

    this.$error.html($message.html())
      .removeClass('hidden');

    this.errors = [];

    return this;
  };

  /**
     * Hide error container.
     *
     * @returns {Manager}
     */
  Manager.prototype.hideError = function () {
    this.$error.addClass('hidden');

    return this;
  };

  /**
     * Clear all errors
     *
     * @returns {Manager}
     */
  Manager.prototype.clearErrors = function () {
    this.$error.html('').addClass('hidden');
    this.errors = [];

    return this;
  };

  /**
     * @returns {jQuery}
     */
  Manager.prototype.getContainer = function () {
    return this.$container;
  };

  /**
     * If room/venue was published and manager has uploaded new photo, after upload page should reload.
     * This method set flag for it.
     *
     * @returns {Manager}
     */
  Manager.prototype.setReloadOnComplete = function () {
    this.reloadOnComplete = true;

    return this;
  };

  /**
     * @returns {boolean}
     */
  Manager.prototype.shouldReloadOnComplete = function () {
    return this.reloadOnComplete;
  };

  /**
     * Refresh page.
     *
     * @returns {void}
     */
  Manager.prototype.reloadPage = function () {
    window.location.reload();
  };

  Manager.prototype.clearAllFiles = function () {
    this.elements = [];
    this.$itemsListContainer.html('');
  };

  return Manager;
});
